import { USER_STATUS_FILTERS, VALIDATION_RULES } from "@sajilni/config";
import {
  ParticipantGroupType,
  prisma,
  type Prisma,
  UserRoleAssignmentEntityType,
} from "client";
import crypto from "crypto";
import jwt from "jsonwebtoken";
import { isAppleReviewOtp } from "../otp/apple-review-otp";
import { consumeOTP, deleteOtpVersion, normalizeQueryIdentifier } from "../otp";
import { isOtpExpired } from "../otp/otp-ttl.pure";
import { getWhiteLabelIdByEventId } from "../white-label";
import { getOrganizationBySlug } from "../organazition";
import {
  assertSingleOrganizationOwner,
  getExistingUserOrganizationIds,
  getOrganizationIdsForOwnerCheck,
} from "./organization-owner-helpers";
import { adoptUnscopedUser, WHITE_LABEL_REQUIRED_MESSAGE } from "./scopedUser";

export * from "./organization-owner-helpers";
export * from "./loggedInUserType";
export * from "./scopedUser";

/**
 * Helper function to build status filter for user queries
 */
const buildStatusFilter = (statusFilter?: USER_STATUS_FILTERS) => {
  if (statusFilter === USER_STATUS_FILTERS.ACTIVE) {
    return { active: true };
  }
  if (statusFilter === USER_STATUS_FILTERS.INACTIVE) {
    return { active: false };
  }
  return {};
};

export const getAllUsers = async (useId: string) => {
  try {
    const users = await prisma.user.findMany();
    return users;
  } catch (error) {
    return { error };
  }
};

export const getAllUsersPaginated = async (
  userId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  searchByName?: string,
  statusFilter?: USER_STATUS_FILTERS,
  lang?: string,
  orderBy?: {
    [key: string]: "asc" | "desc";
  }
) => {
  try {
    const skip = (pageNumber - 1) * pageSize;
    const escapeRegex = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes regex special characters

    const searchParam = searchByName ? escapeRegex(searchByName) : "";
    const filter = searchParam
      ? {
          OR: [
            { name: { contains: searchParam, mode: "insensitive" } },
            { email: { contains: searchParam, mode: "insensitive" } },
            // role name
            {
              userRoleAssignments: {
                some: {
                  role: {
                    name: { contains: searchParam, mode: "insensitive" },
                  },
                },
              },
            },
          ],
        }
      : {};
    // Build status filter
    const statusWhere = buildStatusFilter(statusFilter);
    const users = await prisma.user.findMany({
      // @ts-ignore
      where: {
        ...filter,
        ...statusWhere,
      },
      include: {
        userRoleAssignments: {
          include: {
            role: true,
          },
        },
        profiles: {
          include: {
            roleV2: true,
          },
        },
        roleV2: true,
      },
      skip,
      take: pageSize,
      orderBy: orderBy || { id: "desc" },
    });
    const totalUsers = await prisma.user.count({
      // @ts-ignore
      where: {
        ...filter,
        ...statusWhere,
      },
    });
    return {
      data: users,
      pagination: {
        pageNumber,
        pageSize,
        total: totalUsers,
        totalPages: Math.ceil(totalUsers / pageSize),
        hasNextPage: totalUsers > pageNumber * pageSize,
      },
    };
  } catch (error) {
    return { error };
  }
};

export const getAllUsersPaginatedByWhitLabelId = async (
  userId: string,
  whiteLabelId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  searchByName?: string,
  lang?: string,
  orderBy: Record<string, "asc" | "desc"> = { _id: "desc" },
  statusFilter?: USER_STATUS_FILTERS
) => {
  try {
    const skip = (pageNumber - 1) * pageSize;
    const searchRegex =
      searchByName && typeof searchByName === "string"
        ? searchByName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        : null;

    const statusMatch = buildStatusFilter(statusFilter);
    const pipeline: any[] = [
      {
        $match: {
          accessibleWhiteLabelId: { $oid: whiteLabelId },
          ...statusMatch,
        },
      },
      // Lookup userRoleAssignments
      {
        $lookup: {
          from: "AssignmentModel",
          localField: "_id",
          foreignField: "userId",
          as: "userRoleAssignments",
        },
      },

      // Lookup roles
      {
        $lookup: {
          from: "Role",
          localField: "userRoleAssignments.roleId",
          foreignField: "_id",
          as: "roles",
        },
      },
      {
        $addFields: {
          userRoleAssignments: {
            $map: {
              input: "$userRoleAssignments",
              as: "ura",
              in: {
                $mergeObjects: [
                  "$$ura",
                  {
                    entityIdString: { $toString: "$$ura.entityId" },
                    entityIdObjectId: {
                      $cond: {
                        if: { $eq: ["$$ura.entityType", "ORGANIZATION"] },
                        then: { $toObjectId: "$$ura.entityId" },
                        else: null,
                      },
                    },
                  },
                ],
              },
            },
          },
        },
      },

      {
        $lookup: {
          from: "Profile",
          localField: "_id",
          foreignField: "userId",
          as: "profiles",
        },
      },
      {
        $lookup: {
          from: "RoleV2",
          let: {
            profileRoleV2Ids: "$profiles.roleV2Id", // array from nested path
            topRoleV2Id: "$roleV2Id", // optional scalar
          },
          pipeline: [
            {
              $match: {
                $expr: {
                  $in: [
                    "$_id",
                    {
                      $setUnion: [
                        {
                          $map: {
                            input: { $ifNull: ["$$profileRoleV2Ids", []] },
                            as: "rid",
                            in: {
                              $cond: [
                                { $eq: [{ $type: "$$rid" }, "objectId"] },
                                "$$rid",
                                { $toObjectId: "$$rid" },
                              ],
                            },
                          },
                        },
                        // include top-level roleV2Id if present
                        {
                          $cond: [
                            { $ne: ["$$topRoleV2Id", null] },
                            [
                              {
                                $cond: [
                                  {
                                    $eq: [
                                      { $type: "$$topRoleV2Id" },
                                      "objectId",
                                    ],
                                  },
                                  "$$topRoleV2Id",
                                  { $toObjectId: "$$topRoleV2Id" },
                                ],
                              },
                            ],
                            [],
                          ],
                        },
                      ],
                    },
                  ],
                },
              },
            },
            {
              $project: {
                _id: 1,
                id: { $toString: "$_id" },
                name: 1,
                description: 1,
              },
            },
          ],
          as: "roleV2",
        },
      },
      // Lookup events
      {
        $lookup: {
          from: "Event",
          let: { eventId: "$profiles.eventId" },
          pipeline: [
            {
              $match: {
                $expr: { $eq: ["$_id", "$$eventId"] },
              },
            },
            {
              $lookup: {
                from: "EventTranslations",
                localField: "_id",
                foreignField: "eventId",
                as: "eventTranslations",
              },
            },
            {
              $project: {
                _id: 1,
                name: 1,
                description: 1,
                id: { $toString: "$_id" },
              },
            },
          ],
          as: "events",
        },
      },

      // Lookup organizations
      {
        $lookup: {
          from: "Organization",
          let: {
            organizationIds: {
              $cond: {
                if: { $isArray: "$organizationIds" },
                then: "$organizationIds",
                else: [],
              },
            },
          },
          pipeline: [
            {
              $match: {
                $expr: {
                  $and: [
                    { $ne: [{ $size: "$$organizationIds" }, 0] },
                    { $in: ["$_id", "$$organizationIds"] },
                  ],
                },
              },
            },
            {
              $lookup: {
                from: "OrganizationTranslations",
                localField: "_id",
                foreignField: "organizationId",
                as: "organizationTranslations",
              },
            },
            {
              $project: {
                _id: 1,

                name: { $arrayElemAt: ["$organizationTranslations.name", 0] },
                description: {
                  $arrayElemAt: ["$organizationTranslations.description", 0],
                },
                id: { $toString: "$_id" },
              },
            },
          ],
          as: "organizations",
        },
      },
      {
        $addFields: {
          userRoleAssignments: {
            $map: {
              input: "$userRoleAssignments",
              as: "ura",
              in: {
                $mergeObjects: [
                  "$$ura",
                  {
                    entityId: { $toString: "$$ura.entityId" },
                    roleId: {
                      $toString: "$$ura.roleId",
                    },
                  },
                ],
              },
            },
          },
          id: { $toString: "$_id" },
        },
      },
      {
        $addFields: {
          userRoleAssignments: {
            $map: {
              input: "$userRoleAssignments",
              as: "ura",
              in: {
                $mergeObjects: [
                  "$$ura",
                  {
                    entityId: "$$ura.entityIdString",
                  },
                ],
              },
            },
          },
        },
      },
    ];

    // Add search filters if present
    if (searchRegex) {
      pipeline.push({
        $match: {
          $or: [
            { name: { $regex: searchRegex } },
            { email: { $regex: searchRegex } },
            { "roles.name": { $regex: searchRegex } },
          ],
        },
      });
    }

    // Count total
    const countPipeline = [...pipeline, { $count: "total" }];
    const countResult = await prisma.user.aggregateRaw({
      pipeline: countPipeline,
    });
    const total =
      (countResult?.[0] as { total: number } | undefined)?.total || 0;

    // Apply sort, skip, and limit
    pipeline.push(
      { $sort: convertOrder(orderBy) },
      { $skip: skip },
      { $limit: pageSize }
    );

    const users = await prisma.user.aggregateRaw({ pipeline });

    return {
      data: users,
      pagination: {
        pageNumber,
        pageSize,
        total,
        totalPages: Math.ceil(total / pageSize),
        hasNextPage: total > pageNumber * pageSize,
      },
    };
  } catch (error) {
    console.error("Aggregation error:", error);
    throw error;
  }
};

// Utility to convert Prisma-style orderBy to MongoDB sort object
function convertOrder(orderBy: Record<string, "asc" | "desc">) {
  const mongoSort: Record<string, 1 | -1> = {};
  for (const key in orderBy) {
    mongoSort[key] = orderBy[key] === "asc" ? 1 : -1;
  }
  return mongoSort;
}

export const getAllUsersPaginatedByOrganizationId = async (
  userId: string,
  organizationId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  orderBy?: {
    [key: string]: "asc" | "desc";
  },
  searchByName?: string,
  statusFilter?: USER_STATUS_FILTERS
) => {
  try {
    const skip = (pageNumber - 1) * pageSize;
    const escapeRegex = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes regex special characters

    const searchParam = searchByName ? escapeRegex(searchByName) : "";
    const filter = searchParam
      ? {
        OR: [
          { name: { contains: searchParam, mode: "insensitive" } },
          { email: { contains: searchParam, mode: "insensitive" } },
          // role name
          {
            userRoleAssignments: {
              some: {
                role: {
                  name: { contains: searchParam, mode: "insensitive" },
                },
              },
            },
          },
        ],
      }
      : {};
    // Build status filter
    const statusWhere = buildStatusFilter(statusFilter);
    const users = await prisma.user.findMany({
      where: {
        ...filter,
        ...statusWhere,
        OR: [
          {
            organizations: {
              some: {
                id: organizationId,
              },
            },
          },
          {
            userRoleAssignments: {
              some: {
                entityId: organizationId,
              },
            },
          },
          {
            userRoleAssignments: {
              some: {
                event: {
                  organizations: {
                    some: {
                      id: organizationId,
                    },
                  },
                },
              },
            },
          },
        ],
      },
      include: {
        profiles: {
          where: {
            event: {
              organizations: {
                some: {
                  id: organizationId,
                },
              },
            },
          },
          include: {
            roleV2: true,
          },
        },
        roleV2: true,
        userRoleAssignments: {
          include: {
            role: true,
          },
        },
      },
      skip,
      take: pageSize,
      orderBy: orderBy || { id: "desc" },
    });
    const totalUsers = await prisma.user.count({
      // @ts-ignore
      where: {
        ...filter,
        ...statusWhere,
        OR: [
          {
            userRoleAssignments: {
              some: {
                entityId: organizationId,
              },
            },
          },
          {
            userRoleAssignments: {
              some: {
                event: {
                  organizations: {
                    some: {
                      id: organizationId,
                    },
                  },
                },
              },
            },
          },
        ],
      },
    });
    return {
      data: users,
      pagination: {
        pageNumber,
        pageSize,
        total: totalUsers,
        totalPages: Math.ceil(totalUsers / pageSize),
        hasNextPage: totalUsers > pageNumber * pageSize,
      },
    };
  } catch (error) {
    return { error };
  }
};

export const getUserByemail = async (email: string) => {
  const user = await prisma.user.findFirst({
    where: { email },
  });
  return user;
};

export const getUserByEmailAndWhiteLabelId = async (
  email: string,
  whiteLabelId: string
) => {
  const user = await prisma.user.findUnique({
    where: {
      email_accessibleWhiteLabelId: {
        email,
        accessibleWhiteLabelId: whiteLabelId,
      },
    },
  });
  return user;
};
export const checkUserExist = async (email: string, whiteLabelId: string) => {
  let user;
  user = await prisma.user.findUnique({
    where: {
      email_accessibleWhiteLabelId: {
        email,
        accessibleWhiteLabelId: whiteLabelId,
      },
    },
  });
  if (!user) {
    user = await prisma.user.findFirst({
      where: {
        email,
        userRoleAssignments: {
          some: {
            role: {
              name: "SUPER_ADMIN",
            },
          },
        },
      },
    });
  }
  return user ? true : false;
};

export const getUserById = async (id: string) => {
  const user = await prisma.user.findFirst({
    where: { id },
  });
  return user;
};
export const getUserByEmail = async (email: string) => {
  try {
    const user = await prisma.user.findFirst({
      where: { email: email.toLowerCase() },
      include: {
        roles: {
          include: {
            role: true,
          },
        },
        userRoleAssignments: {
          include: {
            role: true,
            organization: {
              include: {
                whiteLabel: true,
                events: true,
              },
            },
            event: {
              include: {
                whiteLabel: true,
              },
            },
            whiteLabel: true,
          },
        },
      },
    });
    return user;
  } catch (err: any) {
    console.error("Error retrieving user:", err.message);
    throw err;
  }
};
export const getUserByInvitationToken = async (invitationToken: string) => {
  const user = await prisma.user.findFirst({
    where: {
      invitationToken,
      // invitationAcceptedAt: {
      //   not: null,
      // },
    },
  });
  return user;
};
export const updateUserInvitationToken = async (
  email: string,
  whiteLabelId: string,
  invitationToken: string
) => {
  const user = await prisma.user.update({
    where: {
      email_accessibleWhiteLabelId: {
        email,
        accessibleWhiteLabelId: whiteLabelId,
      },
    },
    data: {
      invitationToken,
    },
  });
  return user;
};

export const getUserEvents = async (userId: string, lang?: string) => {
  const user = await prisma.user.findUnique({
    where: { id: userId },
    include: {
      userRoleAssignments: {
        include: {
          whiteLabel: true,
          event: {
            include: {
              venues: {
                include: {
                  translations: lang
                    ? { where: { language: { code: lang } } }
                    : true,
                },
              },
              translations: lang
                ? { where: { language: { code: lang } } }
                : true,
            },
          },
          role: true,
        },
      },
      roleV2: true,
      profiles: {
        include: {
          roleV2: true,
          event: {
            include: {
              venues: {
                where: { deletedAt: null },
                include: {
                  translations: lang
                    ? { where: { language: { code: lang } } }
                    : true,
                },
              },
              translations: lang
                ? { where: { language: { code: lang } } }
                : true,
              whiteLabel: {
                select: { subdomain: true },
              },
            },
          },
        },
      },
    },
  });

  // Early return if user doesn't exist
  if (!user) {
    return [];
  }

  // Check if user is SUPER_ADMIN at user level
  const isSuperAdmin =
    user?.userRoleAssignments?.some(
      (assignment) => assignment.role?.name === "SUPER_ADMIN"
    ) || user?.roleV2?.name === "SUPER_ADMIN";

  // If SUPER_ADMIN, fetch and return all events
  if (isSuperAdmin) {
    const events = await prisma.event.findMany({
      include: {
        venues: {
          where: { deletedAt: null },
          include: {
            translations: lang ? { where: { language: { code: lang } } } : true,
          },
        },
        translations: lang ? { where: { language: { code: lang } } } : true,
        whiteLabel: {
          select: { subdomain: true },
        },
      },
    });

    return events.map((event) => ({
      ...event,
      ...event.translations?.[0],
      venues: event.venues?.map((venue: any) => ({
        ...venue,
        ...venue?.translations?.[0],
        id: venue.id,
      })),
      id: event.id,
    }));
  }

  // Collect unique events using Map for efficient deduplication
  const eventMap = new Map();

  // Add events directly assigned to user via userRoleAssignments
  user.userRoleAssignments.forEach((assignment) => {
    if (assignment.event?.id) {
      eventMap.set(assignment.event.id, assignment.event);
    }
  });

  // Add events from profiles where user has a roleV2 assigned
  user.profiles.forEach((profile) => {
    // Only include profiles that have a roleV2 assigned and an associated event
    if (profile.roleV2Id && profile.event) {
      eventMap.set(profile.event.id, profile.event);
    }
  });

  // Get whitelabel IDs from assignments
  const whiteLabelIds = user.userRoleAssignments
    .filter((assignment) => assignment.whiteLabel?.id)
    .map((assignment) => assignment.whiteLabel!.id);

  // Fetch and add events from whitelabels if there are any
  if (whiteLabelIds.length > 0) {
    const whiteLabels = await prisma.whiteLabel.findMany({
      where: {
        id: { in: whiteLabelIds },
      },
      include: {
        events: {
          include: {
            venues: {
              where: { deletedAt: null },
              include: {
                translations: lang
                  ? { where: { language: { code: lang } } }
                  : true,
              },
            },
            translations: lang ? { where: { language: { code: lang } } } : true,
            whiteLabel: {
              select: { subdomain: true },
            },
          },
        },
      },
    });

    // Add events from whitelabels to the map
    whiteLabels.forEach((whiteLabel) => {
      whiteLabel.events.forEach((event) => {
        if (event.id) {
          eventMap.set(event.id, event);
        }
      });
    });
  }

  // Return unique events with translations and venues
  return Array.from(eventMap.values()).map((event) => ({
    ...event,
    ...event?.translations?.[0],
    venues: event?.venues?.map((venue: any) => ({
      ...venue,
      ...venue?.translations?.[0],
      id: venue?.id,
    })),
    id: event?.id,
  }));
};

export const getUserWhitelabels = async (userId: string, lang?: string) => {
  const user = await prisma.user.findUnique({
    where: { id: userId },
    include: {
      userRoleAssignments: {
        include: {
          whiteLabel: true,
          event: {
            include: {
              whiteLabel: true,
            },
          },
          role: true,
        },
      },
    },
  });

  // Early return if user doesn't exist
  if (!user) {
    return [];
  }

  // Check if user is SUPER_ADMIN
  const isSuperAdmin = user.userRoleAssignments.some(
    (assignment) => assignment.role?.name === "SUPER_ADMIN"
  );

  // If SUPER_ADMIN, fetch and return all whitelabels
  if (isSuperAdmin) {
    return await prisma.whiteLabel.findMany();
  }

  // Collect unique whitelabels using Map for efficient deduplication
  const whiteLabelMap = new Map();

  // Add whitelabels directly assigned to user
  user.userRoleAssignments.forEach((assignment) => {
    if (assignment.whiteLabel) {
      whiteLabelMap.set(assignment.whiteLabel.id, assignment.whiteLabel);
    }
  });

  // Add whitelabels from events
  user.userRoleAssignments.forEach((assignment) => {
    if (assignment.event?.whiteLabel) {
      whiteLabelMap.set(
        assignment.event.whiteLabel.id,
        assignment.event.whiteLabel
      );
    }
  });

  // Return unique whitelabels as array
  return Array.from(whiteLabelMap.values());
};

export const getUserWhitelabelsByEmail = async (
  email: string,
  password?: string,
  lang?: string
) => {
  let where: Prisma.UserWhereInput = { email: email.toLowerCase() };
  if (password) {
    const md5Hasher = crypto.createHmac(
      "md5",
      process.env.SECRET_HASH_PASSWORD as string
    );
    const hashedPassword = md5Hasher.update(password).digest("hex");
    where = {
      email: email.toLowerCase(),
      password: hashedPassword,
    };
  }
  const user = await prisma.user.findMany({
    where,
    include: {
      accessibleWhiteLabel: true,
      userRoleAssignments: {
        include: {
          whiteLabel: true,
          event: {
            include: {
              whiteLabel: true,
            },
          },
          role: true,
        },
      },
    },
  });

  // If password was provided and no user found, return "invalid"
  if (password && (user.length === 0 || !user)) {
    return "invalid";
  }

  // Check if user is SUPER_ADMIN
  const isSuperAdmin = user.some((user) =>
    user.userRoleAssignments?.some(
      (assignment) => assignment.role?.name === "SUPER_ADMIN"
    )
  );

  // If SUPER_ADMIN, fetch and return all whitelabels
  if (isSuperAdmin) {
    return await prisma.whiteLabel.findMany();
  }

  // Collect unique whitelabels using Map for efficient deduplication
  const whiteLabelMap = new Map();

  // Add whitelabels directly assigned to user
  // user.forEach((user) => {
  //   user.userRoleAssignments.forEach((assignment) => {
  //     if (assignment.whiteLabel) {
  //       whiteLabelMap.set(assignment.whiteLabel.id, assignment.whiteLabel);
  //     }
  //   });
  // });

  // // Add whitelabels from events
  // user.forEach((user) => {
  //   user.userRoleAssignments.forEach((assignment) => {
  //     if (assignment.event?.whiteLabel) {
  //       whiteLabelMap.set(
  //         assignment.event.whiteLabel.id,
  //         assignment.event.whiteLabel
  //       );
  //     }
  //   });
  // });
  // Collect unique whitelabels from accessibleWhiteLabel
  user.forEach((user) => {
    if (user.accessibleWhiteLabel) {
      whiteLabelMap.set(
        user.accessibleWhiteLabel.id,
        user.accessibleWhiteLabel
      );
    }
  });

  // Return unique whitelabels as array
  return Array.from(whiteLabelMap.values());
};

export const getEventDoorManagementUshrer = async (eventId: string) => {
  return await prisma.user.findMany({
    where: {
      AND: [
        {
          accessibleWhiteLabel: {
            events: {
              some: { id: eventId },
            },
          },
        },
        {
          OR: [
            {
              profiles: {
                some: {
                  AND: [
                    {
                      event: {
                        id: eventId,
                      },
                    },
                    {
                      roleV2: {
                        name: "DOOR_AGENT",
                      },
                    },
                  ],
                },
              },
            },
            {
              roleV2: {
                name: "DOOR_AGENT",
              },
            },
            {
              userRoleAssignments: {
                some: {
                  role: { name: "DOOR_AGENT" },
                  entityId: eventId,
                },
              },
            },
          ],
        },
      ],
    },
  });
};

export const login = async (
  email: string,
  password?: string,
  token?: string,
  accessibleWhiteLabelId?: string
) => {
  try {
    email = email.toLowerCase();

    // First, try to find user by email and WhiteLabel ID if provided
    let user = null;
    if (accessibleWhiteLabelId) {
      user = await prisma.user.findFirst({
        where: {
          email: email.toLowerCase(),
          accessibleWhiteLabelId: accessibleWhiteLabelId,
        },
        include: {
          userRoleAssignments: {
            include: {
              role: true,
            },
          },
        },
      });
    }

    // If not found with WhiteLabel ID or no WhiteLabel ID provided, try finding user by email only
    if (!user) {
      user = await prisma.user.findFirst({
        where: {
          email: email.toLowerCase(),
          OR: [
            // Check old role system
            {
              userRoleAssignments: {
                some: {
                  role: { name: "SUPER_ADMIN" },
                },
              },
            },
            // Check new roleV2 system
            {
              roleV2: {
                name: "SUPER_ADMIN",
              },
            },
          ],
        },
        include: {
          userRoleAssignments: {
            include: {
              role: true,
            },
          },
          roleV2: true,
        },
      });

      // If found but WhiteLabel ID was provided and doesn't match, return error
      if (
        user &&
        accessibleWhiteLabelId &&
        user.accessibleWhiteLabelId !== accessibleWhiteLabelId &&
        !user.userRoleAssignments.some(
          (assignment) => assignment.role?.name === "SUPER_ADMIN"
        ) &&
        user.roleV2?.name !== "SUPER_ADMIN"
      ) {
        return {
          error: "Please login from your organization's admin portal",
        };
      }
    }

    if (!user) {
      // Handle case when user is not found
      return {
        error: "Please provide valid email or password",
      };
    }
    if (!user.active) {
      return {
        error: "User is deactivated",
      };
    }

    if (token) {
      // First, try to verify token as an admin JWT magic link
      try {
        const decoded = jwt.verify(
          token,
          process.env.SECRET_HASH_PASSWORD as string
        ) as any;
        if (decoded && decoded.email?.toLowerCase() === email) {
          return {
            ...user,
            roles: user.userRoleAssignments,
          };
        }
      } catch (e) {
        // Not a valid JWT or signature mismatch, fallback to ParticipantInvitation logic
      }

      // Fallback: check if it's a participant invitation token
      const participantInvitation =
        await prisma.participantInvitation.findUnique({
          where: { token },
        });

      if (participantInvitation?.email.toLowerCase() !== email) {
        return {
          error: "Please provide valid email",
        };
      }
      return {
        ...user,
        roles: user.userRoleAssignments,
      };
    }

    if (!password) {
      return {
        error: "Please provide valid email or password",
      };
    }
    const md5Hasher = crypto.createHmac(
      "md5",
      process.env.SECRET_HASH_PASSWORD as string
    );
    const hashedPassword = md5Hasher.update(password).digest("hex");
    // Compare the provided password with the hashed password from the database
    const passwordMatch = hashedPassword === user.password;

    if (passwordMatch) {
      await prisma.user.update({
        where: { id: user.id },
        data: { lastLoginAt: new Date() },
      });

      // Passwords match, user is authenticated
      return { ...user, roles: user.userRoleAssignments };
    } else {
      // Passwords do not match, handle authentication failure
      return {
        error: "Please provide valid email or password",
      };
    }
  } catch (error) {
    console.error("error", error);
    throw error;
  }
};
export const loginByOTP = async (
  email: string,
  otp: number,
  eventId: string | undefined | null,
  whiteLabelId?: string | null
) => {
  try {
    const normalizedEmail = normalizeQueryIdentifier(email);

    let accessibleWhiteLabelId = whiteLabelId || undefined;
    const eventObjectId =
      eventId && /^[a-f\d]{24}$/i.test(eventId) ? eventId : undefined;
    if (!accessibleWhiteLabelId && eventObjectId) {
      accessibleWhiteLabelId =
        (await getWhiteLabelIdByEventId(eventObjectId)) ?? undefined;
    }

    if (!accessibleWhiteLabelId) {
      return { error: "Invalid login request" };
    }

    const user = await prisma.user.findUnique({
      where: {
        email_accessibleWhiteLabelId: {
          email: normalizedEmail,
          accessibleWhiteLabelId,
        },
      },
      include: {
        userRoleAssignments: {
          include: {
            role: true,
          },
        },
        profiles: {
          where: {
            active: true,
            event: {
              whiteLabelId: accessibleWhiteLabelId,
            },
          },
          include: {
            event: true,
          },
          orderBy: {
            createdAt: "desc",
          },
        },
        networking: true,
      },
    });
    if (!user) {
      return {
        error: "Please provide valid email",
      };
    }
    if (!user.active) {
      return {
        error: "User is deactivated",
      };
    }

    const selectedProfile = eventObjectId
      ? user.profiles.find((profile) => profile.eventId === eventObjectId)
      : user.profiles[0];
    if (eventObjectId && !selectedProfile) {
      return {
        error: "Please register",
      };
    }

    const otpIdentifier = normalizedEmail;
    // do not call verifyOTP — this path is the only consumer for NextAuth login
    if (!isAppleReviewOtp(otpIdentifier, otp)) {
      const otpRow = await prisma.oTP.findFirst({
        where: { identifier: otpIdentifier, code: otp },
      });
      if (!otpRow || otpRow.verified) {
        return {
          error: "Please provide valid OTP",
        };
      }
      if (isOtpExpired(otpRow.createdAt)) {
        const deleted = await deleteOtpVersion(otpRow);
        return deleted.count === 1
          ? { error: "OTP_EXPIRED" }
          : { error: "Please provide valid OTP" };
      }
      if (!(await consumeOTP(otpRow))) {
        return { error: "Please provide valid OTP" };
      }
    }

    await prisma.user.update({
      where: { id: user.id },
      data: { lastLoginAt: new Date() },
    });
    return {
      ...user,
      profiles: selectedProfile ?? null,
      roles: user.userRoleAssignments,
      totalEvents: user.profiles.length,
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

interface Assignments {
  roleId: string;
  entityId?: string | null;
  entityType: UserRoleAssignmentEntityType;
}

interface RoleV2Assignment {
  roleV2Id: string;
  scope: "USER" | "PROFILE";
  eventId?: string;
  whiteLabelId?: string;
  organizationIds?: string[];
}

const resolveUserLevelRoleV2Assignment = (
  roleV2Assignments: RoleV2Assignment[]
) => {
  const userLevelAssignments = roleV2Assignments.filter(
    (assignment) => assignment.scope === "USER"
  );
  return (
    userLevelAssignments.find(
      (assignment) => (assignment.organizationIds?.length ?? 0) > 0
    ) ?? userLevelAssignments[0]
  );
};

// CREATE
export const createUser = async (
  userId: string,
  userPayload: Prisma.UserCreateInput,
  whiteLabelId?: string,
  organizationId?: string,
  assignments?: Assignments[],
  roleV2Assignments?: RoleV2Assignment[]
) => {
  try {
    if (!userPayload.email) {
      throw new Error("Please provide an email");
    }
    userPayload.email = userPayload.email?.toLowerCase();
    if (!VALIDATION_RULES.isEmail.test(userPayload.email)) {
      throw new Error("Please provide a valid email");
    }

    userPayload = {
      ...userPayload,
      email: userPayload.email.toLocaleLowerCase(),
      phoneNumber: userPayload.phoneNumber,
      name: userPayload.name,
      roles: userPayload.roles,
    };

    if (userPayload.password) {
      if (!VALIDATION_RULES.isPassword.test(userPayload.password)) {
        throw new Error(
          "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one special character, and one number"
        );
      }
      const md5Hasher = crypto.createHmac(
        "md5",
        process.env["SECRET_HASH_PASSWORD"] as string
      );
      const hashedPassword = md5Hasher
        .update(userPayload.password)
        .digest("hex");
      userPayload.password = hashedPassword;
    }
    if (organizationId) {
      userPayload.organizations = {
        connect: {
          id: organizationId ?? undefined,
        },
      };
    }

    // Handle RoleV2 assignments
    if (roleV2Assignments && roleV2Assignments.length > 0) {
      const userLevelAssignment =
        resolveUserLevelRoleV2Assignment(roleV2Assignments);
      if (userLevelAssignment) {
        userPayload.roleV2 = {
          connect: { id: userLevelAssignment.roleV2Id },
        };
        // Set accessibleWhiteLabelId if provided
        if (userLevelAssignment.whiteLabelId) {
          userPayload.accessibleWhiteLabel = {
            connect: { id: userLevelAssignment.whiteLabelId },
          };
        }
        // Set organizations if provided
        const organizationIdsFromAssignments = getOrganizationIdsForOwnerCheck({
          assignmentOrganizationIds: roleV2Assignments
            .filter((assignment) => assignment.scope === "USER")
            .flatMap((assignment) => assignment.organizationIds ?? []),
          organizationId,
        });
        if (organizationIdsFromAssignments.length > 0) {
          userPayload.organizations = {
            connect: organizationIdsFromAssignments.map((id) => ({
              id,
            })),
          };
        }

        await assertSingleOrganizationOwner(
          userLevelAssignment.roleV2Id,
          organizationIdsFromAssignments
        );
      }
    }

    // Keep legacy assignment model for backward compatibility
    if (assignments && assignments.length >= 1) {
      const hasAssignmentsWithoutRole = assignments.find(
        (assignment) => !assignment.roleId && !assignment.entityType
      );
      if (hasAssignmentsWithoutRole) {
        throw new Error(`can not have assignment without roleId`);
      }
    }

    const userCreationData = {
      ...userPayload,
      userRoleAssignments: assignments?.length
        ? {
          createMany: {
            data: assignments.map((assignment) => ({
              roleId: assignment.roleId,
              ...(assignment.entityId
                ? { entityId: assignment.entityId }
                : {}),
              entityType: assignment.entityType,
            })),
          },
        }
        : undefined,
    };
    let allowUnscoped = false;
    const userLevelAssignment = roleV2Assignments?.length
      ? resolveUserLevelRoleV2Assignment(roleV2Assignments)
      : undefined;
    if (userLevelAssignment) {
      const role = await prisma.roleV2.findUnique({
        where: { id: userLevelAssignment.roleV2Id },
        select: { name: true },
      });
      allowUnscoped = role?.name === "SUPER_ADMIN";
    }
    if (!whiteLabelId && organizationId) {
      const organization = await prisma.organization.findUnique({
        where: { id: organizationId },
        select: { whiteLabelId: true },
      });
      whiteLabelId = organization?.whiteLabelId ?? whiteLabelId;
    }
    if (!whiteLabelId && !allowUnscoped) {
      throw new Error(WHITE_LABEL_REQUIRED_MESSAGE);
    }
    if (whiteLabelId) {
      await adoptUnscopedUser(userPayload.email, whiteLabelId);
    }

    let existingUser;
    if (whiteLabelId) {
      existingUser = await prisma.user.findUnique({
        where: {
          email_accessibleWhiteLabelId: {
            email: userPayload.email?.toLowerCase(),
            accessibleWhiteLabelId: whiteLabelId,
          },
        },
      });
    } else {
      existingUser = await prisma.user.findFirst({
        where: {
          email: userPayload.email?.toLowerCase(),
          roleV2: {
            name: "SUPER_ADMIN",
          },
        },
      });
    }
    if (existingUser) {
      throw new Error("A user with this email already exists");
    }

    const user = await prisma.user.create({
      data: {
        ...userCreationData,
        ...(whiteLabelId
          ? { accessibleWhiteLabel: { connect: { id: whiteLabelId } } }
          : {}),
      },
    });

    // Create profiles for event-level RoleV2 assignments
    if (roleV2Assignments && roleV2Assignments.length > 0) {
      const profileLevelAssignments = roleV2Assignments.filter(
        (a) => a.scope === "PROFILE" && a.eventId
      );

      for (const assignment of profileLevelAssignments) {
        const existingProfile = await prisma.profile.findFirst({
          where: {
            userId: user.id,
            eventId: assignment.eventId,
          },
        });
        if (existingProfile) {
          await prisma.profile.update({
            where: { id: existingProfile?.id },
            data: {
              userId: user.id,
              roleV2Id: assignment.roleV2Id,
            },
          });
        } else {
          await prisma.profile.create({
            data: {
              userId: user.id,
              eventId: assignment.eventId!,
              roleV2Id: assignment.roleV2Id,
            },
          });
        }
      }
    }

    return user;
  } catch (err) {
    console.error("error in createUser", err);
    throw new Error(
      err instanceof Error ? err.message : "Unknown error occurred"
    );
  }
};

// UPDATE
export const updateUser = async (
  userId: string,
  id: string,
  updatedData: Prisma.UserUpdateInput,
  oldPassword?: string,
  organizationId?: string,
  assignments?: Assignments[],
  roleV2Assignments?: RoleV2Assignment[]
) => {
  try {
    const userData = await getUserById(id);
    if (
      updatedData?.email &&
      !VALIDATION_RULES.isEmail.test(updatedData?.email as string)
    ) {
      throw new Error("Invalid email");
    } else if (updatedData.email && typeof updatedData.email === "string")
      updatedData.email = updatedData?.email?.toLowerCase();

    if (updatedData.password && typeof updatedData.password === "string") {
      if (oldPassword && userData?.password) {
        const md5Hasher = crypto.createHmac(
          "md5",
          process.env.SECRET_HASH_PASSWORD as string
        );
        const hashedOldPassword = md5Hasher.update(oldPassword).digest("hex");
        if (hashedOldPassword !== userData?.password) {
          throw new Error(
            "The old password provided does not match the current password."
          );
        }
      }
      if (!VALIDATION_RULES.isPassword.test(updatedData.password)) {
        throw new Error(
          "Password must be at least 8 characters long and contain at least one lowercase letter, one uppercase letter, one digit (0-9), and one special character among @$!%*?&"
        );
      }

      const md5Hasher = crypto.createHmac(
        "md5",
        process.env.SECRET_HASH_PASSWORD as string
      );
      const hashedPassword = md5Hasher
        .update(updatedData?.password)
        .digest("hex");
      updatedData.password = hashedPassword;
    }
    if (organizationId) {
      updatedData.organizations = {
        connect: {
          id: organizationId ? organizationId : undefined,
        },
      };
    }

    // Handle RoleV2 assignments
    if (roleV2Assignments && roleV2Assignments.length > 0) {
      const profileLevelAssignments = roleV2Assignments.filter(
        (a) => a.scope === "PROFILE" && a.eventId
      );
      const userLevelAssignment =
        resolveUserLevelRoleV2Assignment(roleV2Assignments);
      if (userLevelAssignment) {
        updatedData.roleV2 = {
          connect: { id: userLevelAssignment.roleV2Id },
        };
        // Set accessibleWhiteLabelId if provided
        const whiteLabelIdFromAssignments = roleV2Assignments.find(
          (assignment) => assignment.scope === "USER" && assignment.whiteLabelId
        )?.whiteLabelId;
        if (whiteLabelIdFromAssignments) {
          updatedData.accessibleWhiteLabel = {
            connect: { id: whiteLabelIdFromAssignments },
          };
        } else if (userLevelAssignment.whiteLabelId) {
          updatedData.accessibleWhiteLabel = {
            connect: { id: userLevelAssignment.whiteLabelId },
          };
        }
        const organizationIdsFromAssignments = getOrganizationIdsForOwnerCheck({
          assignmentOrganizationIds: roleV2Assignments
            .filter((assignment) => assignment.scope === "USER")
            .flatMap((assignment) => assignment.organizationIds ?? []),
          organizationId,
        });
        if (organizationIdsFromAssignments.length > 0) {
          updatedData.organizations = {
            set: organizationIdsFromAssignments.map((orgId) => ({
              id: orgId,
            })),
          };
        }

        const organizationIdsForOwnerCheck = getOrganizationIdsForOwnerCheck({
          assignmentOrganizationIds: organizationIdsFromAssignments,
          existingUserOrganizationIds:
            getExistingUserOrganizationIds(userData),
        });

        await assertSingleOrganizationOwner(
          userLevelAssignment.roleV2Id,
          organizationIdsForOwnerCheck,
          id
        );
      } else if (profileLevelAssignments.length > 0) {
        updatedData.roleV2 = {
          disconnect: true,
        };
      }

      // Handle profile-level assignments (delete old ones and create new ones)
      // Create new profiles
      for (const assignment of profileLevelAssignments) {
        const existingProfile = await prisma.profile.findFirst({
          where: {
            userId: id,
            eventId: assignment.eventId,
          },
        });
        if (existingProfile) {
          await prisma.profile.update({
            where: { id: existingProfile.id },
            data: {
              roleV2Id: assignment.roleV2Id,
            },
          });
        } else {
          await prisma.profile.create({
            data: {
              userId: id,
              eventId: assignment.eventId!,
              roleV2Id: assignment.roleV2Id,
            },
          });
        }
      }
    }

    // Keep legacy assignment model for backward compatibility
    if (assignments && assignments.length > 0) {
      if (
        assignments.find(
          (assignment) => !assignment.roleId && !assignment.entityType
        )
      ) {
        throw new Error(`can not have assignment without roleId`);
      }
    }

    const user = await prisma.user.update({
      where: { id },
      data: {
        ...updatedData,
        ...(assignments && assignments.length > 0
          ? {
            userRoleAssignments: {
              deleteMany: {
                entityId: {
                  notIn:
                    assignments
                      ?.map((assignment) => assignment.entityId)
                      .filter((id): id is string => Boolean(id)) || [],
                },
              },
              ...(assignments?.length
                ? {
                  deleteMany: {
                    userId: id,
                  },
                  createMany: {
                    data: assignments.map((assignment) => ({
                      roleId: assignment.roleId,
                      entityType: assignment.entityType,
                      ...(assignment.entityId
                        ? { entityId: assignment.entityId }
                        : {}),
                    })),
                  },
                }
                : {}),
            },
          }
          : {}),
      },
      include: {
        userRoleAssignments: {
          include: {
            role: true,
          },
        },
        roleV2: true,
        profiles: {
          where: {
            roleV2Id: { not: null },
          },
          include: {
            roleV2: true,
          },
        },
      },
    });

    return user;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// update user password
export const updateUserPassword = async (
  id: string,
  password: string,
  invitationAcceptedAt?: Date
) => {
  try {
    if (password) {
      const md5Hasher = crypto.createHmac(
        "md5",
        process.env.SECRET_HASH_PASSWORD as string
      );
      const hashedPassword = md5Hasher.update(password).digest("hex");
      password = hashedPassword;
    }
    const user = await prisma.user.update({
      where: {
        id,
      },
      data: {
        password,
        updatePassword: false,
        invitationAcceptedAt,
      },
    });
    return user;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
// update user password by invitation
export const updateUserPasswordByToken = async (
  email: string,
  password: string,
  whiteLabelId: string,
  invitationAcceptedAt?: Date
) => {
  try {
    if (password) {
      const md5Hasher = crypto.createHmac(
        "md5",
        process.env.SECRET_HASH_PASSWORD as string
      );
      const hashedPassword = md5Hasher.update(password).digest("hex");
      password = hashedPassword;
    }
    const user = await prisma.user.update({
      where: {
        email_accessibleWhiteLabelId: {
          email: email.toLocaleLowerCase(),
          accessibleWhiteLabelId: whiteLabelId,
        },
      },
      data: {
        password,
        updatePassword: true,
        invitationAcceptedAt,
      },
    });
    return user;
  } catch (error) {
    console.error(error);
    throw error;
  }
};
// Org CREATE
export const createOrganizerTeam = async (
  userId: string,
  organizationId: string,
  userPayload: Prisma.UserCreateInput
) => {
  try {
    const viewerRole = await prisma.role.findFirst({
      where: {
        name: "ORGANIZER_VIEWER",
      },
    });

    if (!viewerRole) {
      throw new Error("ORGANIZER_VIEWER role not found");
    }
    const organization = await prisma.organization.findUnique({
      where: {
        id: organizationId,
      },
    });

    let userData = {
      email: userPayload.email?.toLocaleLowerCase(),
      roles: userPayload.roles,
      eventsIds: userPayload.eventsIds,
    };
    const user = await prisma.user.create({
      data: {
        ...userData,
        ...(organization?.whiteLabelId
          ? { accessibleWhiteLabelId: organization.whiteLabelId }
          : {}),
        organizations: {
          connect: { id: organizationId },
        },
        userRoleAssignments: {
          create: {
            roleId: viewerRole.id,
            entityId: organization?.whiteLabelId,
            entityType: UserRoleAssignmentEntityType.WHITE_LABEL,
          },
        },
      },
    });
    return user;
  } catch (err) {
    return err;
  }
};

export const getOrganizerTeam = async (
  userId: string,
  organizationId: string,
  page: number = 1,
  pageSize: number = 10,
  search?: string
) => {
  const skip = (page - 1) * pageSize;
  const take = pageSize;

  let whereClause: Prisma.UserWhereInput = {
    organizations: {
      some: {
        id: organizationId,
      },
    },
  };

  if (search) {
    whereClause = {
      AND: [
        whereClause,
        {
          OR: [
            { name: { contains: search, mode: "insensitive" } },
            { email: { contains: search, mode: "insensitive" } },
          ],
        },
      ],
    };
  }

  const [users, totalUsers] = await Promise.all([
    prisma.user.findMany({
      where: whereClause,
      orderBy: {
        id: "desc",
      },
      skip: skip,
      take: take,
    }),
    prisma.user.count({
      where: whereClause,
    }),
  ]);

  const hasNextPage = skip + take < totalUsers;

  return {
    users,
    hasNextPage,
    totalUsers,
  };
};

export const updateOrganizerTeam = async (
  userId: string,
  id: string,
  updatedData: Prisma.UserUpdateInput
) => {
  try {
    const user = await prisma.user.update({
      where: {
        id,
      },
      data: {
        ...updatedData,
      },
    });
    return user;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const deleteOrganizerTeam = async (userId: string, id: string) => {
  try {
    const user = await prisma.user.delete({
      where: {
        id,
      },
    });
    return user;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const checkIfUserHasAccessToOrganization = async (
  userId: string,
  organizationSlug: string,
  eventId?: string,
  whiteLabelId?: string
) => {
  if (!whiteLabelId) {
    return false;
  }

  const organization = await getOrganizationBySlug(
    organizationSlug,
    whiteLabelId
  );
  if (!organization) {
    return false;
  }

  const user = await prisma.assignmentModel.findMany({
    where: {
      userId: userId,
      OR: [
        {
          entityId: organization.id,
          entityType: UserRoleAssignmentEntityType.ORGANIZATION,
        },
        eventId
          ? {
            organization: {
              eventIds: {
                has: eventId,
              },
            },
          }
          : {},
      ],
    },
  });
  return user.length > 0;
};

export const deleteUser = async (id: string) => {
  await prisma.assignmentModel.deleteMany({
    where: {
      userId: id,
    },
  });
  const user = await prisma.user.delete({
    where: {
      id,
    },
  });
  return user;
};

export const deactivateUser = async (id: string) => {
  const user = await prisma.user.update({
    where: { id },
    data: { active: false },
  });
  return user;
};

export const activateUser = async (id: string) => {
  const user = await prisma.user.update({
    where: { id },
    data: { active: true },
  });
  return user;
};

export const createUserAsViewerOrOrganizer = async (userPayload: {
  email: string;
  phoneNumber: string;
  name: string;
  password: string;
  country: "UAE" | "KSA" | "JO" | "International";
}) => {
  try {
    if (!userPayload.email) {
      throw new Error("Please provide an email");
    }
    userPayload.email = userPayload.email?.toLowerCase();

    if (!VALIDATION_RULES.isEmail.test(userPayload.email)) {
      throw new Error("Please provide a valid email");
    }

    const whiteLabel = await prisma.whiteLabel.findFirst({
      where: {
        name: {
          contains: userPayload.country,
          mode: "insensitive",
        },
      },
    });

    if (!whiteLabel) {
      throw new Error(
        `No white label found for country: ${userPayload.country}`
      );
    }

    const viewerRole = await prisma.role.findFirst({
      where: {
        name: "ORGANIZER_VIEWER",
      },
    });

    if (!viewerRole) {
      throw new Error("ORGANIZER_VIEWER role not found");
    }

    userPayload = {
      ...userPayload,
      email: userPayload.email.toLocaleLowerCase(),
      phoneNumber: userPayload.phoneNumber,
      name: userPayload.name,
    };

    if (userPayload.password) {
      if (!VALIDATION_RULES.isPassword.test(userPayload.password)) {
        throw new Error(
          "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one special character, and one number"
        );
      }
      const md5Hasher = crypto.createHmac(
        "md5",
        process.env["SECRET_HASH_PASSWORD"] as string
      );
      const hashedPassword = md5Hasher
        .update(userPayload.password)
        .digest("hex");
      userPayload.password = hashedPassword;
    }

    const userCreationData = {
      ...userPayload,
    };
    const existingUser = await prisma.user.findUnique({
      where: {
        email_accessibleWhiteLabelId: {
          email: userPayload.email?.toLowerCase(),
          accessibleWhiteLabelId: whiteLabel.id,
        },
      },
    });

    if (existingUser) {
      throw new Error("A user with this email already exists");
    }

    const user = await prisma.user.create({
      data: {
        ...userCreationData,
        accessibleWhiteLabelId: whiteLabel.id,
        userRoleAssignments: {
          create: {
            roleId: viewerRole.id,
            entityId: whiteLabel.id,
            entityType: UserRoleAssignmentEntityType.WHITE_LABEL,
          },
        },
      },
      include: {
        userRoleAssignments: {
          include: {
            role: true,
          },
        },
      },
    });

    return { ...user, accessibleWhiteLabelId: whiteLabel.id };
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : "Unknown error occurred"
    );
  }
};
//TODO: Recheck this logic with toleen
export const getUserRoleAssignment = async (
  userId: string,
  eventId: string
): Promise<ParticipantGroupType> => {
  // Build OR conditions dynamically, filtering out conditions with missing data
  const orConditions = [];

  // Condition 1: Check if user is a participant
  if (userId) {
    orConditions.push({
      participants: {
        some: {
          user: {
            id: userId,
          },
        },
      },
    });
  }

  // Condition 2: Check if user is an exhibitor with companySettings permission and Check if user is an exhibitor with eventDayBadge permission
  if (userId && eventId) {
    orConditions.push(
      {
        exhibitors: {
          some: {
            team: {
              some: {
                id: userId,
                profiles: {
                  some: {
                    eventId: eventId,
                    exhibitorPermissions: {
                      is: { companySettings: true },
                    },
                  },
                },
              },
            },
          },
        },
      },
      {
        exhibitors: {
          some: {
            team: {
              some: {
                id: userId,
                profiles: {
                  some: {
                    eventId: eventId,
                    exhibitorPermissions: {
                      is: { eventDayBadge: true },
                    },
                  },
                },
              },
            },
          },
        },
      }
    );
  }

  // Only query if we have valid OR conditions
  if (orConditions.length === 0) {
    return ParticipantGroupType.VISITORS;
  }

  const participantGroup = await prisma.participantGroup.findFirst({
    where: {
      eventId: eventId,
      OR: orConditions,
    },
    select: {
      type: true,
    },
  });

  return participantGroup?.type || ParticipantGroupType.VISITORS;
};

export const deactivateUserOnEvent = async (
  userId: string,
  eventId: string
) => {
  try {
    const user = await prisma.profile.updateMany({
      where: {
        userId: userId,
        eventId: eventId,
      },
      data: {
        active: false,
      },
    });

    return user;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : "Unknown error occurred"
    );
  }
};
export const activateUserOnEvent = async (userId: string, eventId: string) => {
  try {
    const user = await prisma.profile.updateMany({
      where: {
        userId: userId,
        eventId: eventId,
      },
      data: {
        active: true,
      },
    });

    return user;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : "Unknown error occurred"
    );
  }
};
export const checkUserActiveOnEvent = async (
  userId: string,
  eventId: string
) => {
  try {
    const user = await prisma.profile.findFirst({
      where: {
        userId: userId,
        eventId: eventId,
        active: true,
      },
      select: {
        active: true,
      },
    });

    return user?.active;
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : "Unknown error occurred"
    );
  }
};

export const getAllActiveUsersOnEvent = async (eventId: string) => {
  try {
    const users = await prisma.profile.findMany({
      where: {
        eventId: eventId,
        active: true,
        userId: { not: null },
      },
      select: { userId: true },
    });

    return users.map((p) => p.userId!);
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : "Unknown error occurred"
    );
  }
};

export const getAdminUsersCountByOrganizationId = async (
  organizationId: string
) => {
  const usersCount = await prisma.user.count({
    where: {
      organizations: { some: { id: organizationId } },
      roleV2: {
        name: "ORGANIZATION_OWNER",
      },
    },
  });
  return usersCount;
};
